import React, { useMemo } from 'react';
import DraggableBottomSheet from './DraggableBottomSheet';
import { Account } from '../types';
import ParentAccountCard from './ParentAccountCard';
import { GiCrownCoin } from 'react-icons/gi';

interface AccountSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectAccount: (account: Account | null) => void; // null represents CASH
    accounts: Account[];
}

const AccountSelectionModal: React.FC<AccountSelectionModalProps> = ({
    isOpen,
    onClose,
    onSelectAccount,
    accounts
}) => {
    const groupedSections = useMemo(() => {
        const sortAccounts = (inputAccounts: Account[]) => {
            return [...inputAccounts].sort((a, b) => {
                const nameCompare = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
                if (nameCompare !== 0) return nameCompare;

                const typeCompare = a.type.localeCompare(b.type, undefined, { sensitivity: 'base' });
                if (typeCompare !== 0) return typeCompare;

                return a.id - b.id;
            });
        };

        const groupByParent = (inputAccounts: Account[]) => {
            const sortedAccounts = sortAccounts(inputAccounts);
            const parentAccounts = sortedAccounts.filter(
                (account) => account.parentAccountId === null || account.parentAccountId === undefined
            );

            const childMap = sortedAccounts.reduce((acc, account) => {
                if (account.parentAccountId !== null && account.parentAccountId !== undefined) {
                    if (!acc[account.parentAccountId]) {
                        acc[account.parentAccountId] = [];
                    }
                    acc[account.parentAccountId].push(account);
                }
                return acc;
            }, {} as Record<number, Account[]>);

            return parentAccounts.map((parentAccount) => {
                const combinedChildren = [...(parentAccount.children ?? []), ...(childMap[parentAccount.id] ?? [])];
                const dedupedChildren = Array.from(
                    combinedChildren.reduce((acc, childAccount) => {
                        acc.set(childAccount.id, childAccount);
                        return acc;
                    }, new Map<number, Account>()).values()
                );

                return {
                    ...parentAccount,
                    children: dedupedChildren
                };
            });
        };

        const creditCardAccounts = accounts.filter((account) => account.type === 'CREDIT_CARD');
        const savingsAccounts = accounts.filter((account) => account.type === 'SAVINGS');

        return [
            { title: 'Credit Cards', groupedAccounts: groupByParent(creditCardAccounts) },
            { title: 'Bank Accounts', groupedAccounts: groupByParent(savingsAccounts) }
        ].filter((section) => section.groupedAccounts.length > 0);
    }, [accounts]);

    const hasGroupedAccounts = groupedSections.length > 0;

    const handleSelect = (account: Account | null) => {
        onSelectAccount(account);
        // onClose(); // Keep modal open until amount is entered? No, user wants selection first.
    };

    return (
        <DraggableBottomSheet isOpen={isOpen} onClose={onClose} title="Select Account" maxWidth="24rem">
            <div className="h-full bg-background text-foreground rounded-t-lg flex flex-col min-h-0">
                <div className="px-4 pb-3">
                    <p className="text-sm text-muted-foreground">Choose where this manual transaction entry belongs.</p>
                </div>

                <div className="space-y-3 flex-1 min-h-0 pb-10 overflow-y-auto thin-scrollbar">
                    <div className="px-4">
                        <button
                            type="button"
                            onClick={() => handleSelect(null)}
                            className="w-full text-left p-4 bg-card border border-border/80 rounded-2xl cursor-pointer hover:bg-secondary/80 transition-colors"
                        >
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center border border-border/80 shrink-0">
                                            <GiCrownCoin className="w-5 h-5 text-foreground" />
                                        </div>
                                        <p className="font-semibold text-foreground truncate">Cash</p>
                                    </div>
                                </div>
                            </div>
                        </button>
                    </div>

                    {groupedSections.map((section) => (
                        <div key={section.title} className="space-y-3">
                            <div className="px-4">
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{section.title}</p>
                            </div>

                            <div className="overflow-x-auto thin-scrollbar pb-1">
                                <div className="flex gap-2 whitespace-nowrap w-max px-4">
                                    {section.groupedAccounts.map((parentAccount) => (
                                        <ParentAccountCard
                                            key={parentAccount.id}
                                            parentAccount={parentAccount}
                                            onCardClick={(account) => handleSelect(account)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}

                    {!hasGroupedAccounts && (
                        <div className="px-4">
                            <div className="rounded-2xl border border-dashed border-border/80 p-4 text-sm text-muted-foreground bg-card/50">
                                No savings or credit card accounts found.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </DraggableBottomSheet>
    );
};

export default AccountSelectionModal; 
